/** H5/发言端 glossary HTTP 缓存（按 meetingCode + langCode，D19） */

import type { CrV3GlossaryItem } from '../api/cr-v3-viewer.api'
import { fetchGlossaryList } from '../api/cr-v3-viewer.api'

const CACHE_TTL_MS = 5 * 60 * 1000

interface CacheEntry {
  phrases: string[]
  fetchedAt: number
}

export class CrV3GlossaryCache {
  private readonly entries = new Map<string, CacheEntry>()
  private readonly inflight = new Map<string, Promise<string[]>>()

  private key(meetingCode: string, langCode: string): string {
    return `${meetingCode}:${langCode}`
  }

  /** 从 glossary 项提取 STT PhraseList 短语（源语言 originalText） */
  static phrasesFromItems(items: CrV3GlossaryItem[]): string[] {
    const seen = new Set<string>()
    const out: string[] = []
    for (const item of items) {
      const phrase = (item.originalText || '').trim()
      if (!phrase || seen.has(phrase)) {
        continue
      }
      seen.add(phrase)
      out.push(phrase)
    }
    return out
  }

  getCached(meetingCode: string, langCode: string): string[] | null {
    const entry = this.entries.get(this.key(meetingCode, langCode))
    if (!entry) {
      return null
    }
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
      this.entries.delete(this.key(meetingCode, langCode))
      return null
    }
    return entry.phrases
  }

  async load(meetingCode: string, langCode: string): Promise<string[]> {
    const cached = this.getCached(meetingCode, langCode)
    if (cached) {
      return cached
    }
    const cacheKey = this.key(meetingCode, langCode)
    const pending = this.inflight.get(cacheKey)
    if (pending) {
      return pending
    }
    const task = fetchGlossaryList(meetingCode, langCode)
      .then((items) => {
        const phrases = CrV3GlossaryCache.phrasesFromItems(items)
        this.entries.set(cacheKey, { phrases, fetchedAt: Date.now() })
        return phrases
      })
      .catch(() => [] as string[])
      .finally(() => {
        this.inflight.delete(cacheKey)
      })
    this.inflight.set(cacheKey, task)
    return task
  }
}
